/**
 * Replay manager — loads replay data via fetch() and controls
 * the browser-side ReplayEngine. Replaces the WebSocket + server setup.
 */

import { ReplayEngine } from "./replay-engine.js";
import type { ReplayFile, RunInfo, ServerMessage } from "./types.js";

type ManifestEntry = {
  id: string;
  path: string;
};

export class ReplayManager {
  private engine: ReplayEngine | null = null;
  private runs: RunInfo[] = [];
  private onMessage: (msg: ServerMessage) => void;
  private basePath: string;

  constructor(onMessage: (msg: ServerMessage) => void) {
    this.onMessage = onMessage;
    // Resolve base path for fetch — works for both dev (/) and built (./data/)
    this.basePath = import.meta.env.BASE_URL ?? "./";
  }

  async loadManifest(): Promise<RunInfo[]> {
    try {
      const response = await fetch(`${this.basePath}data/manifest.json`);
      const manifest = (await response.json()) as ManifestEntry[];

      const runs: RunInfo[] = [];
      for (const entry of manifest) {
        try {
          const replayResponse = await fetch(`${this.basePath}${entry.path}`);
          const replayFile = (await replayResponse.json()) as ReplayFile;
          runs.push({
            id: entry.id,
            scenario: replayFile.meta.scenario,
            timestamp: replayFile.meta.timestamp,
            commitHash: replayFile.meta.commitHash,
            totalDurationMs: replayFile.meta.totalDurationMs,
          });
        } catch {
          // Skip broken replay files
        }
      }

      this.runs = runs;
      return runs;
    } catch {
      this.runs = [];
      return [];
    }
  }

  getRuns(): RunInfo[] {
    return this.runs;
  }

  async startReplay(runId: string, speed = 4): Promise<string | null> {
    this.stop();

    const manifest = await this.fetchManifest();
    const entry = manifest.find((e) => e.id === runId);
    if (!entry) {
      return `Replay not found: ${runId}`;
    }

    try {
      const response = await fetch(`${this.basePath}${entry.path}`);
      const replayFile = (await response.json()) as ReplayFile;
      this.engine = new ReplayEngine(replayFile, this.onMessage, speed);
      this.engine.start();
      return null;
    } catch (err) {
      return `Failed to load replay: ${err}`;
    }
  }

  stop() {
    if (!this.engine) return;
    this.engine.stop();
    this.engine = null;
  }

  pause() {
    this.engine?.pause();
  }

  resume() {
    this.engine?.resume();
  }

  setSpeed(speed: number) {
    this.engine?.setSpeed(speed);
  }

  seek(position: number) {
    this.engine?.seek(position);
  }

  setDemoMode(enabled: boolean) {
    this.engine?.setDemoMode(enabled);
  }

  private async fetchManifest(): Promise<ManifestEntry[]> {
    try {
      const response = await fetch(`${this.basePath}data/manifest.json`);
      return (await response.json()) as ManifestEntry[];
    } catch {
      return [];
    }
  }
}
